import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, Provider, inject } from '@angular/core';
import { AppComponent } from './app.component';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';
import { AdminGuard, AuthGuard, SegmentService } from '@skychute/shared-services';
import { environment } from '../environments/environment';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { onError } from '@apollo/client/link/error';
import { ApolloLink, FetchResult, from, InMemoryCache } from '@apollo/client/core';
import { JwtModule } from '@auth0/angular-jwt';
import { ToastrModule } from 'ngx-toastr';
import { UiCommonModule } from '@skychute/ui/common';
import { CurrencyPipe } from '@angular/common';
import { shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Observable as ZenObservable } from 'zen-observable-ts';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { UiLoaderModule } from '@skychute/ui/loader';
import { Graphql_Query_Log_Insert_Input } from '@skychute/schema';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ProjectreTitleStrategy } from '@skychute/ui-models';
import { AgentQrCodesModule } from "./pages/agent-qr-codes/agent-qr-codes.module";
import { QrCodeLandingPageModule } from "./pages/qr-code-landing-page/qr-code-landing-page.module";

const routes: Routes = [
  {
    path: '',
    redirectTo: 'pages/projects',
    pathMatch: 'full',
  },
  {
    path: 'changelog',
    loadChildren: () => import('./changelog/changelog.module').then((m) => m.ChangeLogModule),
  },
  {
    path: 'admin',
    title: 'Admin',
    loadChildren: () => import('@skychute/ui/admin').then((m) => m.UiAdminModule),
    canActivate: [AdminGuard],
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth-page/auth-page.module').then((m) => m.AuthPageModule),
  },
  {
    path: 'oauth',
    loadChildren: () => import('./oauth-page/oauth-page.module').then((m) => m.OAuthPageModule),
  },
  {
    path: 'pages',
    loadChildren: () => import('./pages/pages.module').then((m) => m.PagesModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'partner-portal',
    loadChildren: () => import('./pages/partner/partner.module').then((m) => m.PartnerModule),
  },
  {
    path: 'buyer',
    loadChildren: () => import('./pages/buyer/buyer.module').then((m) => m.BuyerModule),
  },
  {
    path: 'agent-qr-codes/landing',
    loadChildren: () => import('./pages/qr-code-landing-page/qr-code-landing-page.module').then((m) => m.QrCodeLandingPageModule),
  },
  // temporary routes
  {
    path: 'file-upload-demo',
    loadChildren: () =>
      import('./file-upload-demo/file-upload-demo.module').then((m) => m.FileUploadDemoModule),
  },
  {
    path: 'technical',
    loadChildren: () =>
      import('./technical-page/technical-page.module').then((m) => m.TechnicalPageModule),
  },
  {
    path: 'tc',
    loadChildren: () =>
      import('./terms-and-conditions-page/terms-and-conditions-page.module').then(
        (m) => m.TermsAndConditionsPageModule,
      ),
  },
  {
    path: 'sales-advice',
    loadChildren: () =>
      import('../../../../libs/ui/sales-advice/src/lib/ui-sales-advice.module').then(
        (m) => m.UiSalesAdviceModule,
      ),
  },
];

const errorLinkApollo = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.map(({ message, locations, path }) =>
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
    );
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

export function jwtTokenGetter(): string {
  return localStorage.getItem('token');
}

let graphQLWsLink: GraphQLWsLink;

function getGraphQLWsLink(): GraphQLWsLink {
  const hasuraUrl = `${environment.services.data.endpoint}${environment.services.data.path}`;
  const wsHasuraUrl = hasuraUrl.replace(/^http/, 'ws');
  return new GraphQLWsLink(
    createClient({
      url: wsHasuraUrl,
      lazy: true,
      retryAttempts: 1,
      connectionParams: () => {
        let token = '';
        if (sessionStorage.getItem('impersonateToken')) {
          token = sessionStorage.getItem('impersonateToken');
        } else if (localStorage.getItem('token')) {
          token = localStorage.getItem('token');
        }
        return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      },
    }),
  );
}

const appProviders: Provider[] = [
  CurrencyPipe,
  // all components should inject environment via:
  // constructor(
  //   @Inject('environment')
  //   environment
  // ) {
  // this.environment = environment
  // }
  {
    provide: TitleStrategy,
    useClass: ProjectreTitleStrategy,
  },
  {
    provide: 'environment',
    useValue: environment,
  },
  {
    provide: GraphQLWsLink,
    useFactory: () => {
      return graphQLWsLink;
    },
  },
  {
    provide: APOLLO_OPTIONS,
    useFactory() {
      /**
       * Function to make a sound
       */
      function beep() {
        const snd = new Audio(
          'data:audio/wav;base64,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',
        );
        snd.play();
      }

      // to track GraphQL query count with the same name
      let requestCount: { [key: string]: number } = {};
      let timeout;

      // webSocketLink = getWebSocketLink();
      graphQLWsLink = getGraphQLWsLink();
      const webSocketLink = graphQLWsLink;

      //
      // Log out user if token expired or wrong, and redirect to login page
      //
      const jwtMiddleware = onError((data) => {
        console.log(data);
        const regExp =
          /JWSError|JWTExpired|connection_init failed|connection_error|header is expected|forbidden|no subscriptions exist/gi;
        if (data?.networkError?.message) {
          console.log('network error', data?.networkError);
          if (data.networkError.message.match(regExp)) {
            // clear session so after redirect, user can log in
            // if we do not clear session here, old token will be used for a login request
            localStorage.removeItem('token');
            // do a full app refresh, due to the fact that we can not re-connect Apollo client
            // with new credentials at runtime
            const returnPath = document.location.pathname;
            const returnSearch = document.location.search;
            const origin = document.location.origin;

            // redirect to token refresh page, try to recover access token if possible
            // it will auto redirect to /auth/login in case of any errors
            document.location.href = `${origin}/auth/refresh?return=${returnPath}${returnSearch}`;
            return;
          }
        }
      });

      /**
       * This middleware tracks all GraphQL requests and their responses
       * calculates duration for every request and prints them in the dev tools console
       * requests which take longer than 3 seconds are styled in red, others are green
       * To enable, enter: DEBUG=1 in the Chrome dev tools console
       * To disable, enter: DEBUG=0 in the Chrome dev tools console, or refresh the page
       */
      const testMiddleware = new ApolloLink((operation, forward) => {
        // Debug mode is enabled if you type "DEBUG=1" in the dev tools and hit enter
        // or it's enabled by default on localhost addresses
        const isDebug = !!window?.['DEBUG'];
        // const isDebug = true;
        const opName = operation.operationName;
        const query = operation.query.loc.source.body;
        const variables = operation.variables;

        // this is not required I guess as subscription should be detected by RegExp below
        // but just to be on a safe side one more time.
        const subscriptions: string[] = [
          'dealEnvelope',
          'getAttachments',
          'getBuyerNav',
          'getDealById',
          'getDealDepositStatusDetails',
          'getDealStatusDetails',
          'getHoldingDepositDetailsByDealID',
          'getListingStatusByDeal',
          'getPaymentInfoByDealId',
          'getReservationNav',
          'reservationListingStatus',
          'subAttachmentById',
          'subscribeDealEnvelope',
          'subscribeToListingStatus',
          'hasuraLogQueries', // this one pollutes DEBUG logs
        ];

        // Don't capture stats for subscriptions, this doesn't make sense
        // subscriptions run for a long time, and can not give use useful info on performance
        // they may also not works correctly if we pass them through this middleware
        // as we're doing conversion zenToRx and rxToZen which can introduce some issues
        if (subscriptions.includes(opName) || !!query.match(/subscription/gi)) {
          if (isDebug) {
            console.log(`⬆️ ${opName} not trackable (subscription)`);
          }
          return forward(operation);
        } else {
          if (isDebug) {
            console.log(`⬆️ ${opName}`);
          }
        }

        const started = Date.now();

        requestCount[opName] = requestCount?.[opName] ? requestCount[opName] + 1 : 1;

        // after 10 seconds print summary of GraphQL requests and their count
        // this way we can detect multiple requests of the same name
        // and make optimisations
        if (!timeout && isDebug) {
          timeout = setTimeout(() => {
            console.log(
              'Request count:',
              Object.keys(requestCount).length,
              JSON.stringify(requestCount, null, 4),
            );
            requestCount = {};
            clearTimeout(timeout);
            timeout = undefined;
          }, 10000);
        }

        const zenToRx = <T>(zenObservable: any): Observable<T> => {
          return new Observable((observer) => zenObservable.subscribe(observer));
        };
        const forwardReturn = zenToRx<FetchResult>(forward(operation)).pipe(shareReplay(1));
        forwardReturn.subscribe((resp) => {
          const ended = Date.now();
          const durationSec = (ended - started) / 1000;
          let color = 'color:green;';
          if (durationSec > 3.0) {
            color = 'color:red;';
          }
          const codeStyle = color;
          const headerStyle = `font-weight: bold;${color}`;
          const importantStyle = 'font-weight: bold; color: red;';
          if (isDebug) {
            console.log(`%c--- QUERY ${opName} took ${durationSec} sec ---`, headerStyle);
            console.log(`%c${query}`, codeStyle);
            console.log(`--- VARS for ${opName} ---`);
            const vars = JSON.stringify(variables, null, 4);
            if (vars.match(/null/gi)) {
              console.log('%c --- HASURA null value in VARS ---', importantStyle);
              beep();
            }
            console.log(vars);
            console.log(`--- RESP for ${opName} ---`);
            console.log(JSON.stringify(resp, null, 4));
          }

          // gather stats to be sent to the frontend
          if (environment.services.data.logQueries === 'true') {
            const logKey = '_x_hasura_logs_';
            if (window[logKey] === undefined) {
              window[logKey] = [];
            }

            // don't log hasuraLogQueries mutation
            if (opName !== 'hasuraLogQueries') {
              const logItem: Graphql_Query_Log_Insert_Input = {
                op_name: opName,
                op_content: query,
                duration: ended - started,
                started_at: started,
                finished_at: ended,
                op_vars: JSON.stringify(variables, null, 2),
              };
              window[logKey].push(logItem);
            }
          }
        });
        return ZenObservable.from(forwardReturn as any);
      });

      return {
        link: from([jwtMiddleware, testMiddleware, webSocketLink]),
        cache: new InMemoryCache({
          typePolicies: {
            country: {
              keyFields: ['value'],
            },
            deal: {
              keyFields: ['id'],
            },
            deal_by_pk: {
              keyFields: ['id'],
            },
            variation_type_category: {
              keyFields: ['id'],
            },
          },
        }),
        options: {
          errorLink: errorLinkApollo,
        },
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'ignore',
          },
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
        },
      };
    },
  },
];

import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { MarkdownModule } from 'ngx-markdown';

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(routes, {
            scrollPositionRestoration: 'enabled',
        }),
        ReactiveFormsModule,
        MatSnackBarModule,
        UiCommonModule,
        ToastrModule.forRoot({
            positionClass: 'toast-top-right',
            preventDuplicates: true,
            timeOut: 2000,
            tapToDismiss: false,
        }),
        JwtModule.forRoot({
            config: {
                tokenGetter: jwtTokenGetter,
            },
        }),
        UiLoaderModule,
        MatProgressBarModule,
        MarkdownModule.forRoot(),
        RecaptchaV3Module], providers: [...appProviders, { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha_v3_site_key }, Apollo, provideHttpClient(withInterceptorsFromDi()), provideCharts(withDefaultRegisterables())] })
export class AppModule {
  private segment = inject(SegmentService);

  constructor() {
    this.segment.identifyUser(localStorage.getItem('token')).then();
  }
}
